import React from "react";
import PartialCircle from "./PartialCircle";
import clsx from "clsx";
import {createUseStyles} from "react-jss";
import {adjustScoreForTime, ProcessedResponse, TriviaStatus} from "../../state/state";
import {QuestionDefinition} from "../../state/questions";
import {TimeUnit, useCountdown} from "../../hooks/useCountdown";
import {isUndefined} from "engage-common/utils";
import {useTheme} from "react-jss";
import {Theme} from "engage-client/theme";
import Color from "color";

const useStyles = createUseStyles<Theme>((theme) => ({
	countdown: {
		position: "relative",
	},
	progressRing: {
		position: "absolute",
		top: 0,
		left: 0,
		zIndex: 2,
		width: props => props.size,
		height: props => props.size,
	},
	lowerRing: {
		position: "absolute",
		top: props => props.thicknessDifference / 2,
		left: props => props.thicknessDifference / 2,
		zIndex: 1,
		width: props => props.size - props.thicknessDifference,
		height: props => props.size - props.thicknessDifference,
		opacity: "20%"
	},
	inside: {
		margin: "1px",
		color: "white",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		flexDirection: "column",
		borderRadius:"50%",
		width: props => props.size,
		height: props => props.size,
	},
	timeContainer: {
		lineHeight: props => `${props.size / 2.2}px`,
		fontSize: props => `${props.size / 2.2}px`,
	},
	timeLabel: {
		lineHeight: props => `${props.size / 140 * 18}px`,
		fontSize: props => `${props.size / 140 * 18}px`,
		letterSpacing: 0,
		fontWeight: props => props.labelWeight,
		transition: "font-weight, opacity .2s ease-in-out",
		color: "white",
	},
}));

type CountdownProps = {
	start: number
	duration: number
	pause?: number
	size?: number
	className?: string
	delayed?: boolean
	state?: TriviaStatus
	question?: QuestionDefinition
	playerResponse?: ProcessedResponse
	thickness?: number
	color?: string
}

export default function Countdown(props:CountdownProps) {
	const theme:Theme = useTheme();

	const {className, start, duration, pause, size = 140, delayed = false, state, question, playerResponse, thickness = 7, color} = props;
	const smallThickness = thickness / 5;
	const thicknessDifference = thickness - smallThickness;
	const countdown = useCountdown(start, duration, pause, TimeUnit.MILLISECONDS);
	const progress = countdown / duration;

	let pointsLabel:string|undefined = undefined;
	let scoreFrozen = false;
	if(state === TriviaStatus.QuestionSubmit && question) {
		let adjustedScore;
		if(question.multiSubmit) {
			if(playerResponse && playerResponse.score > 0) {
				adjustedScore = playerResponse.score;
				scoreFrozen = true;
			} else {
				adjustedScore = adjustScoreForTime(question,100, duration-countdown, duration);
			}

		} else {
			const elapsed = playerResponse?.timeElapsed ? playerResponse.timeElapsed : duration - countdown;
			adjustedScore = adjustScoreForTime(question, 100, elapsed, duration);
			scoreFrozen = !isUndefined(playerResponse);
		}
		pointsLabel = question.poll ? "" : `${adjustedScore} pts`;
	}

	const styleProps = {
		labelWeight: scoreFrozen ? 900 : 400,
		size,
		thicknessDifference
	};
	const classes = useStyles(styleProps);
	const value = Math.ceil(countdown/1000);
	const progressRingColor = color ?? theme.questionPrompt.timeRemainderColor;
	const delayedColor = theme.questionPrompt.delayedColor ?? Color(progressRingColor).darken(0.5).toString();
	const seconds = Math.ceil(countdown / 1000);

	return (
		<div className={clsx(classes.countdown, className)} data-qa="prompt-countdown" data-value={value}>
			<div className={classes.inside}>
				{!delayed ?
					<>
					<div className={classes.timeContainer}>
						{value}
					</div>
					<div className={classes.timeLabel}>{pointsLabel}</div>
					</>
					: 
					<div className={classes.timeContainer}>
						{seconds <= 3 ? seconds : null}
					</div>
				}
			</div>
			<div className={classes.lowerRing}>
				<PartialCircle size={size-thicknessDifference} thickness={smallThickness} percentage={100} color={"white"}/>
			</div>
			<div className={classes.progressRing}>
				<PartialCircle size={size} thickness={thickness} percentage={progress} color={delayed ? delayedColor : progressRingColor}/>
			</div>
		</div>
	);
}